/**
 * useMapLayers.ts
 * Base layer functionality for map
 */

import { useDispatch, useSelector } from "react-redux";

import type { Map } from "maplibre-gl";
import type { FeatureCollection, Geometry } from "geojson";

import { SPATIAL_TYPE_TO_LAYER_TYPE } from "@/constants/dataset";
import {
  CIRCE_RADIUS_SOURCE_ID,
  COUNTRY_SOURCE_ID,
  ROUTING_SOURCE_ID,
  SUBLAYERS
} from "@/constants/map";
import { type RootState, store } from "@/store";
import {
  reorderDatasets,
  resetDatasetToHistoryDataset
} from "@/store/slice/datasetSlice";
import { clearCurrRunningAnalysisId } from "@/store/slice/spatialSlice";
import type { SpatialAggregationType } from "@/types/common";
import type { Dataset, DatasetData } from "@/types/dataset";
import { LayerType } from "@/types/map";
import {
  convertDatasetResponseToFeatureCollection,
  getAvailableDatasets
} from "@/utils/dataset";
import { removeAllSourceLayerWithPrefix } from "@/utils/map";

type UseMapLayersProps = {
  map: Map;
  removeAllMarkers: () => void;
  handleAddPointsLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddPolygonLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddH3GeohashLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
};

export const useMapLayers = ({
  map,
  removeAllMarkers,
  handleAddPointsLayer,
  handleAddPolygonLayer,
  handleAddH3GeohashLayer
}: UseMapLayersProps) => {
  const dispatch = useDispatch();
  const { datasets, history } = useSelector(
    (state: RootState) => state.dataset
  );
  const availableDatasets = getAvailableDatasets(datasets);

  const showOnlyLayer = (layerIdToKeepVisible: string) => {
    if (!map) return;

    const style = map.getStyle();

    if (!style?.layers) return;
    style.layers.forEach((layer) => {
      // Get the original visibility from the style definition
      const originalVisibility = layer.layout?.visibility || "visible";

      if (
        (layer.id.includes("-fill") ||
          layer.id.includes("-outline") ||
          layer.id.includes("-circle") ||
          layer.id.includes("-count") ||
          layer.id.includes("-unclustered-points") ||
          layer.id.includes("-label") ||
          layer.id.includes("-rank")) &&
        !layer.id.startsWith(layerIdToKeepVisible)
      ) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      } else if (
        originalVisibility !== "none" ||
        (layer.id.startsWith(layerIdToKeepVisible) &&
          !layer.id.includes("-label") &&
          !layer.id.includes("-rank"))
      ) {
        // Only set to visible if the original style was visible (don't override "none")
        map.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });
  };

  const restoreAllLayersVisibility = () => {
    if (!map) return;

    const style = map.getStyle();
    if (!style?.layers) return;

    Object.entries(availableDatasets).forEach(([datasetId, dataset]) => {
      const baseVisible = dataset.style.visibility !== "none";
      const labelVisible =
        baseVisible && (dataset.style?.labelConfig?.show_label ?? false);
      const rankVisible =
        baseVisible && (dataset.style?.rankConfig?.isEnabled ?? false);

      style.layers.forEach((layer) => {
        if (!layer.id.startsWith(datasetId)) return;
        let finalVisibility = baseVisible ? "visible" : "none";

        // Override for label layers
        if (layer.id.endsWith("-label")) {
          finalVisibility = labelVisible ? "visible" : "none";
        }

        // Override for rank layers
        if (layer.id.endsWith("-rank")) {
          finalVisibility = rankVisible ? "visible" : "none";
        }

        map.setLayoutProperty(layer.id, "visibility", finalVisibility);
      });
    });
  };

  const hideLayersByIds = (layerIdsToHide: string[]) => {
    if (!map) return;

    const style = map.getStyle();

    if (!style?.layers) return;
    style.layers.forEach((layer) => {
      // Get the original visibility from the style definition
      const originalVisibility = layer.layout?.visibility || "visible";

      if (layerIdsToHide.some((id) => layer.id.startsWith(id))) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      } else if (originalVisibility !== "none") {
        // Only set to visible if the original style was visible (don't override "none")
        map.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });
  };

  const handleRemoveSourceLayer = (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => {
    if (!map) return;

    const layerType = SPATIAL_TYPE_TO_LAYER_TYPE[spatialType];
    const sublayers = SUBLAYERS(datasetId);
    Object.values(sublayers?.[layerType] ?? []).forEach((layer) => {
      if (map?.getLayer(layer)) map.removeLayer(layer);
    });

    const generalSource = map.getSource(datasetId);
    const profilingSource =
      layerType === LayerType.PROFILING &&
      map.getSource(`${datasetId}-polygon`);

    let sourceId: string | null = null;

    if (generalSource) {
      sourceId = datasetId;
    } else if (profilingSource) {
      sourceId = `${datasetId}-polygon`;
    }

    if (sourceId) map.removeSource(sourceId);
  };

  const handleResetLayerOrder = (layerOrders: string[]) => {
    const dispatch = store.dispatch;
    dispatch(reorderDatasets(layerOrders));

    if (!map) return;

    const style = map.getStyle();
    if (!style?.layers) return;

    // Create a mapping of all layers by their source
    const layersBySource: { [sourceId: string]: string[] } = {};
    style.layers.forEach((layer) => {
      if ("source" in layer && layer.source) {
        if (!layersBySource[layer.source]) {
          layersBySource[layer.source] = [];
        }
        layersBySource[layer.source].push(layer.id);
      }
    });

    // Process layers in reverse order of visualization
    // This ensures the first dataset in layerOrders appears on top
    for (let i = layerOrders.length - 1; i >= 0; i--) {
      const sourceId = layerOrders[i];
      const layers = layersBySource[sourceId] || [];

      // Move each layer associated with this source to the top
      layers.forEach((layerId) => {
        map.moveLayer(layerId);
      });
    }
  };

  const handleHideAllLayerVisibility = () => {
    if (!map) return;

    const style = map.getStyle();

    if (!style?.layers) return;

    // Hide all layers by setting their visibility to 'none'
    style.layers.forEach((layer) => {
      if (
        layer.id.includes("-fill") ||
        layer.id.includes("-outline") ||
        layer.id.includes("-circle") ||
        layer.id.includes("-count") ||
        layer.id.includes("-unclustered-points")
      ) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      }
    });
  };

  const handleClearAnalysisResultView = () => {
    removeAllMarkers();
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, COUNTRY_SOURCE_ID, []);
  };

  const handleResetLayerData = (
    datasets: { [id: string]: Dataset },
    rawDatasets: {
      [id: string]: DatasetData;
    }
  ) => {
    // Get the map instance
    if (!map) return;

    const availableHistoryDatasets = getAvailableDatasets(datasets);

    // Iterate through each dataset and reset its properties
    Object.values(availableHistoryDatasets).forEach((dataset) => {
      const data = rawDatasets[dataset.data.id];

      const featureCollection = convertDatasetResponseToFeatureCollection(
        data,
        dataset.categories
      );
      const spatialType = dataset.properties.spatialType;
      if (spatialType === LayerType.POINT) {
        handleAddPointsLayer(dataset, featureCollection);
      } else if (
        spatialType === LayerType.POLYGON ||
        spatialType === LayerType.ADMINISTRATIVE
      ) {
        handleAddPolygonLayer(dataset, featureCollection);
      } else if (
        spatialType === LayerType.H3 ||
        spatialType === LayerType.GEOHASH
      ) {
        handleAddH3GeohashLayer(dataset, featureCollection);
      }
    });
  };

  const handleResetDatasetToHistoryDataset = () => {
    // Get the difference between current datasets and history datasets
    const getDifferenceBetweenDatasets = () => {
      const currentDatasetIds = Object.keys(availableDatasets);
      const historyDatasetIds = history ? Object.keys(history.datasets) : [];
      return currentDatasetIds.filter((id) => !historyDatasetIds.includes(id));
    };

    // Calculate differences before resetting
    const addedDatasets = getDifferenceBetweenDatasets();

    // Remove layers for datasets that were added after the history snapshot
    addedDatasets.forEach((datasetId) => {
      const dataset = datasets[datasetId];
      handleRemoveSourceLayer(datasetId, dataset.properties.spatialType);
    });

    dispatch(resetDatasetToHistoryDataset());
    dispatch(clearCurrRunningAnalysisId());
    handleResetLayerOrder(history.visualizationOrder);
    handleResetLayerData(history.datasets, history.rawDatasets);
  };

  return {
    handleClearAnalysisResultView,
    handleResetDatasetToHistoryDataset,
    handleRemoveSourceLayer,
    handleResetLayerOrder,
    handleHideAllLayerVisibility,
    handleResetLayerData,
    showOnlyLayer,
    hideLayersByIds,
    restoreAllLayersVisibility
  };
};

export default useMapLayers;
